"use client";

import { Trash2, Loader2, AlertTriangle, CheckCircle } from "lucide-react";
import { useState } from "react";

import { Button } from "@/app/_module/components/ui/button";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogAction,
  ResponsiveAlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { verifyMFAForAction, resendSMSForAction } from "@/app/_module/services/auth";
import { SimplifiedMFAModal } from "@/app/_module/components/SimplifiedMFAModal";

export const DeleteAccount = function () {
  const { branding } = useBranding();
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const { user } = useUserContext();

  // Check if user has MFA enabled
  const hasMFAEnabled = user?.user?.mfaEnabled;

  const handleDeleteAccountRequest = () => {
    setShowDeleteAccountDialog(true);
  };

  const handleProceedToConfirm = () => {
    setShowDeleteAccountDialog(false);

    // If user has MFA enabled, require verification first
    if (hasMFAEnabled) {
      setShowMFAModal(true);
    } else {
      // No MFA, go directly to confirmation
      setShowConfirmDialog(true);
    }
  };

  const handleMFAVerified = () => {
    setShowMFAModal(false);
    setShowConfirmDialog(true);
  };

  const handleMFAVerify = async (code: string, method: 'totp' | 'sms'): Promise<boolean> => {
    try {
      const verified = await verifyMFAForAction(code, method);
      return verified;
    } catch (error) {
      return false;
    }
  };

  const handleDeleteAccount = async () => {
    if (confirmText !== "DELETE") {
      toast({
        title: "Confirmation Required",
        description: "Please type DELETE to confirm account deletion.",
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);

    try {
      await handleApiCall(async () => {
        return await api.post({
          url: getApiUrl("/users/me/request-deactivation"),
          body: { reason: reason || "User requested account deletion" },
          auth: true,
        });
      });

      setShowConfirmDialog(false);
      setShowSuccessDialog(true);

      // Log out after a short delay
      setTimeout(async () => {
        try {
          // Set logout reason so sign-in page shows appropriate message
          if (typeof window !== 'undefined') {
            localStorage.setItem('insuracentral_logout_reason', 'account_deactivated');
          }
          const { logout } = await import("@/app/_module/services/auth");
          await logout();
        } catch {
          // Redirect to login page if logout fails
          if (typeof window !== 'undefined') {
            localStorage.setItem('insuracentral_logout_reason', 'account_deactivated');
          }
          window.location.href = "/auth/sign-in";
        }
      }, 3000);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete account. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      {/* Delete Account Card */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="text-red-600">Danger Zone</CardTitle>
          <CardDescription>
            Permanently delete your account and all associated data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <p className="text-sm text-gray-600">
                Once your account is deactivated, you will not be able to access your account.
                Contact support within 30 days to reactivate. Your data will be retained for 180 days before permanent deletion.
              </p>
            </div>
            <Button
              variant="outline"
              className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
              onClick={handleDeleteAccountRequest}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Account
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step 1: Initial Warning Dialog */}
      <AlertDialog open={showDeleteAccountDialog} onOpenChange={setShowDeleteAccountDialog}>
        <ResponsiveAlertDialogContent size="md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Delete Your Account?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-3">
              <p>
                This action will deactivate your account and schedule it for permanent deletion.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-amber-800 font-medium text-sm mb-2">What will happen:</p>
                <ul className="text-amber-700 text-sm space-y-1 list-disc list-inside">
                  <li>You will be immediately logged out</li>
                  <li>Your account will be deactivated</li>
                  <li>You have 30 days to contact support to reactivate</li>
                  <li>Your data will be retained for 180 days</li>
                  <li>After 180 days, all data will be permanently deleted</li>
                </ul>
              </div>
              <p className="text-sm text-gray-600">
                To reactivate your account, contact{" "}
                <a href={`mailto:${branding.supportEmail}`} className="text-blue-600 hover:underline font-medium">
                  {branding.supportEmail}
                </a>
                {" "}within 30 days.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleProceedToConfirm}
            >
              Continue
            </Button>
          </AlertDialogFooter>
        </ResponsiveAlertDialogContent>
      </AlertDialog>

      {/* Step 2: Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <ResponsiveAlertDialogContent size="md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Confirm Account Deletion</AlertDialogTitle>
            <AlertDialogDescription className="text-left">
              <p className="mb-4">
                This is your final confirmation. Please type <strong>DELETE</strong> below to confirm.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="reason">Reason for leaving (optional)</Label>
              <Textarea
                id="reason"
                placeholder="Help us improve by sharing why you're leaving..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="min-h-[80px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm">
                Type <span className="font-bold text-red-600">DELETE</span> to confirm
              </Label>
              <Input
                id="confirm"
                placeholder="DELETE"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                className="font-mono"
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={isDeleting || confirmText !== "DELETE"}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete My Account
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </ResponsiveAlertDialogContent>
      </AlertDialog>

      {/* Step 3: Success Dialog */}
      <AlertDialog open={showSuccessDialog} onOpenChange={() => {}}>
        <ResponsiveAlertDialogContent size="md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-green-600 flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Account Deletion Requested
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-3">
              <p>
                Your account has been deactivated and scheduled for permanent deletion.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-blue-900 font-medium text-sm mb-2">Changed your mind?</p>
                <p className="text-blue-800 text-sm">
                  Contact our support team at{" "}
                  <a href={`mailto:${branding.supportEmail}`} className="font-medium underline">
                    {branding.supportEmail}
                  </a>
                  {" "}within 30 days to reactivate your account.
                </p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-amber-800 text-sm">
                  Your data will be retained for 180 days before permanent deletion.
                </p>
              </div>
              <p className="text-sm text-gray-500">
                You will be logged out automatically...
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
        </ResponsiveAlertDialogContent>
      </AlertDialog>

      {/* MFA Verification Modal */}
      <SimplifiedMFAModal
        isOpen={showMFAModal}
        onClose={() => setShowMFAModal(false)}
        onVerified={handleMFAVerified}
        onVerify={handleMFAVerify}
        onSendSMS={resendSMSForAction}
        title="Verify Identity to Delete Account"
        description="For security, please verify your identity with 2FA before deleting your account."
      />
    </>
  );
};
